import { useNavigator } from '@repo/toolkit/use-navigator'
import { ActivityIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getActivity } from '../../../api/ols/activities/get-activity'
import { updateActivity } from '../../../api/ols/activities/update-activity'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import ActivityForm from '../../../components/pages/ols/activity/activity-form'
import { Paths } from '../../../lib/constants/path'
import useAuth from '../../../lib/hooks/use-auth'
import { CurrencyEnum } from '../../../lib/types/enums/common/currency.enum'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'
import { mapActivityToInput } from './util'

const ActivityUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const olId = user?.ol?.id
  const currency = user?.ol?.currency ?? CurrencyEnum.EUR

  const { mutateAsync } = useMutation({
    mutationFn: updateActivity,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ exact: true, queryKey: ['activities', id, olId] })
      toast.success('Activity updated successfully')
    }
  })

  const { data, isPending } = useQuery({
    queryKey: ['activities', id, olId],
    queryFn: () => {
      if (!id || isNaN(+id) || !olId) return
      return getActivity({ id: +id, olId })
    }
  })

  if (isPending) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={data?.name} icon={ActivityIcon} />
      <div className={'h-4'} />
      <ActivityForm
        currency={currency}
        defaultValues={mapActivityToInput(data)}
        buttonsConfig={updateButtonsConfig({
          onCancel: () => back(Paths.OL_PERSONAL_SPACE_ACTIVITY_LIST_PAGE),
          onUpdate: (values) => {
            if (!id || isNaN(+id) || !olId) return
            return mutateAsync({ id: +id, ...values, olId })
          },
          onUpdateThenCreate: async (values) => {
            if (!id || isNaN(+id) || !olId) return
            await mutateAsync({ id: +id, ...values, olId })
            navigate(Paths.OL_PERSONAL_SPACE_ACTIVITY_CREATE_PAGE)
          },
          onUpdateThenList: async (values) => {
            if (!id || isNaN(+id) || !olId) return
            await mutateAsync({ id: +id, ...values, olId })
            back(Paths.OL_PERSONAL_SPACE_ACTIVITY_LIST_PAGE)
          },
          isEnglish: true
        })}
      />
    </div>
  )
}

export default ActivityUpdatePage
